import { config } from "../../config";
import {
  PUBLIC_READINESS_ROWS,
  PUBLIC_READINESS_VERIFIED_AT,
  type PublicReadinessRow,
  type PublicReadinessState,
} from "../../data/publicReadiness";
import Brandmark from "./Brandmark";
import { CheckIcon, ChevronIcon } from "./LandingIcons";

interface LandingReadinessProps {
  onRequestAccess: () => void;
}

const FEATURED_CAPABILITIES = [
  "Environment version save and rollback (GitHub)",
  "AG OPC value and namespace readback",
  "Per-developer sandbox provisioning",
  "Native Vision Client testing",
  "Discovery audit (project health)",
  "CI/CD with signed deployment and rollback",
] as const;

const ACCESS_QUALIFIERS = [
  "Ignition version and project shape",
  "Gateway, client, and data-source dependencies",
  "The change or workflow you need to test",
] as const;

const DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "long",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});

export default function LandingReadiness({ onRequestAccess }: LandingReadinessProps) {
  const rows = FEATURED_CAPABILITIES.map((capability) =>
    PUBLIC_READINESS_ROWS.find((row) => row.capability === capability),
  ).filter((row): row is PublicReadinessRow => Boolean(row));

  const reviewedAt = DATE_FORMATTER.format(
    new Date(`${PUBLIC_READINESS_VERIFIED_AT}T00:00:00Z`),
  );

  return (
    <>
      <section className="oc-readiness" id="readiness" aria-labelledby="readiness-title">
        <div className="oc-site-shell oc-readiness__grid">
          <div className="oc-readiness__intro">
            <p className="oc-eyebrow">Current product truth</p>
            <h2 id="readiness-title">Preview, with the limits stated plainly.</h2>
            <p>
              OrbitCell separates implemented software, support-resource
              readiness, Gateway commissioning, and retained customer-path
              proof. 8.1 and 8.3 stay visible as separate evidence lanes.
            </p>
            <span className="oc-readiness__reviewed">Last reviewed {reviewedAt}</span>
          </div>

          <ReadinessTable rows={rows} />
          <ReadinessCards rows={rows} />
          <AllReadiness />
        </div>
      </section>

      <section className="oc-access" aria-labelledby="access-title">
        <div className="oc-site-shell">
          <div className="oc-access__panel">
            <div className="oc-access__copy">
              <p className="oc-eyebrow">Closed technical preview</p>
              <h2 id="access-title">Tell us what your project needs to prove.</h2>
              <p>
                The closed preview is for teams actively building on Ignition
                8.1 or 8.3. We review your project needs against the current
                supported path before onboarding.
              </p>
            </div>
            <ul className="oc-access__qualifiers">
              {ACCESS_QUALIFIERS.map((item) => (
                <li key={item}>
                  <span><CheckIcon /></span>
                  {item}
                </li>
              ))}
            </ul>
            <div className="oc-access__action">
              <button
                className="oc-button oc-button--primary oc-button--large"
                type="button"
                onClick={onRequestAccess}
              >
                Request preview access
              </button>
              <p>
                Access is readiness-gated and project-specific. Joining the
                waitlist is not a promise of immediate beta access.
              </p>
            </div>
          </div>
        </div>
      </section>

      <LandingFooter />
    </>
  );
}

function AllReadiness() {
  return (
    <details className="oc-readiness-all">
      <summary>
        <span>Review all {PUBLIC_READINESS_ROWS.length} capabilities</span>
        <ChevronIcon />
      </summary>
      <div className="oc-readiness-all__content">
        <ReadinessTable rows={PUBLIC_READINESS_ROWS} />
        <ReadinessCards rows={PUBLIC_READINESS_ROWS} />
      </div>
    </details>
  );
}

function ReadinessTable({ rows }: { rows: PublicReadinessRow[] }) {
  return (
    <div className="oc-readiness-table-wrap">
      <table className="oc-readiness-table">
        <thead>
          <tr>
            <th scope="col">Capability</th>
            <th scope="col">8.1</th>
            <th scope="col">8.3</th>
            <th scope="col">Current limitation</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.capability}>
              <th scope="row">{shortCapability(row.capability)}</th>
              <td><ReadinessState state={row.ignition81} /></td>
              <td><ReadinessState state={row.ignition83} /></td>
              <td>{row.limitation}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReadinessCards({ rows }: { rows: PublicReadinessRow[] }) {
  return (
    <div className="oc-readiness-cards">
      {rows.map((row) => (
        <details key={row.capability}>
          <summary>
            <span>{shortCapability(row.capability)}</span>
            <span className="oc-readiness-cards__versions">
              <span><b>8.1</b><ReadinessState state={row.ignition81} short /></span>
              <span><b>8.3</b><ReadinessState state={row.ignition83} short /></span>
            </span>
            <ChevronIcon />
          </summary>
          <div>
            <p><b>8.1</b> <ReadinessState state={row.ignition81} /></p>
            <p><b>8.3</b> <ReadinessState state={row.ignition83} /></p>
            <p>{row.limitation}</p>
          </div>
        </details>
      ))}
    </div>
  );
}

function ReadinessState({
  state,
  short = false,
}: {
  state: PublicReadinessState;
  short?: boolean;
}) {
  const label = state === "ready_now" ? "Ready now" : state === "preview" ? "Preview" : "Not ready";
  return (
    <span className={`oc-readiness-state is-${state}`} title={label}>
      <i aria-hidden="true" />
      {short ? <span className="sr-only">{label}</span> : label}
    </span>
  );
}

function shortCapability(capability: string) {
  const names: Record<string, string> = {
    "AG OPC value and namespace readback": "AG OPC namespace + value readback",
    "Per-developer sandbox provisioning": "Isolated environment provisioning",
    "Discovery audit (project health)": "Discovery audit",
    "CI/CD with signed deployment and rollback": "Signed deployment + rollback",
  };
  return names[capability] ?? capability;
}

function LandingFooter() {
  const loginHref = `${config.appBaseUrl || ""}/login`;

  return (
    <footer className="oc-site-footer">
      <div className="oc-site-shell oc-site-footer__inner">
        <div className="oc-site-footer__brand">
          <Brandmark size={34} ariaLabel="OrbitCell" />
          <div>
            <strong>OrbitCell</strong>
            <span>Development and automated testing workspace for Ignition teams.</span>
          </div>
        </div>
        <nav aria-label="Footer">
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms</a>
          <a href={loginHref}>Sign in</a>
        </nav>
        <p>
          &copy; 2026{" "}
          <a href="https://joatlabs.dev" target="_blank" rel="noreferrer">
            JoatLabs.dev
          </a>
        </p>
      </div>
    </footer>
  );
}
